import React, { useState, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowBack, Person, LocalHospital, Help } from '@mui/icons-material';
import { validateFirstName, validateLastName, validatePassword, validatePasswordConfirmation, PASSWORD_RULE_HINT } from '../validation/authSchemas';
import { CARESEEKER_LABEL } from '../utils/roleLabels';
import { useFormValidation } from '../hooks/useFormValidation';
import { authService } from '../services/authService';
import { useApp } from '../context/AppContext';
import { getLoginRedirectRoute } from '../utils/navigation';
import { useNavigate } from 'react-router-dom';

interface RegisterFormProps {
  email: string;
  onBack: () => void;
  onSuccess: () => void;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({
  email,
  onBack,
  onSuccess,
}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedRole, setSelectedRole] = useState<'PATIENT' | 'CAREGIVER'>('PATIENT');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const { errors, validateField, validateFieldWithDependency, clearError, hasErrors } = useFormValidation();
  const { state, dispatch } = useApp();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);

  const handleFirstNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFirstName(value);
    setApiError(null);
    
    if (value.trim()) {
      validateField('firstName', value, validateFirstName);
    } else {
      clearError('firstName');
    }
  };

  const handleLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setLastName(value);
    setApiError(null);
    
    if (value.trim()) {
      validateField('lastName', value, validateLastName);
    } else {
      clearError('lastName');
    }
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setPassword(value);
    setApiError(null);
    
    if (value.trim()) {
      validateField('password', value, validatePassword);
      // Re-validate confirm password when password changes
      if (confirmPassword.trim()) {
        validateFieldWithDependency('confirmPassword', confirmPassword, validatePasswordConfirmation, value);
      }
    } else {
      clearError('password');
      clearError('confirmPassword');
    }
  };

  const handleConfirmPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setConfirmPassword(value);
    setApiError(null);
    
    if (value.trim()) {
      validateFieldWithDependency('confirmPassword', value, validatePasswordConfirmation, password);
    } else {
      clearError('confirmPassword');
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (hasErrors || !firstName.trim() || !lastName.trim() || !password.trim() || !confirmPassword.trim()) {
      return;
    }

    setIsLoading(true);
    setApiError(null);

    try {
      const response = await authService.register(email, password, firstName, lastName, selectedRole);
      
      // Store token and user data
      authService.setToken(response.data.token);
      dispatch({
        type: 'SET_USER',
        payload: {
          id: response.data.user.id,
          email: response.data.user.email,
          displayName: `${response.data.user.firstName} ${response.data.user.lastName}`,
          firstName: response.data.user.firstName,
          lastName: response.data.user.lastName,
          role: response.data.user.role,
        },
      });
      
      // Navigate to appropriate dashboard based on role
      const redirectRoute = getLoginRedirectRoute(response.data.user, state.redirectUrl);
      navigate(redirectRoute);
      
      onSuccess();
    } catch (error) {
      setApiError(error instanceof Error ? error.message : 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Handle form submission
  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleSubmit(event);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <Box
            component="img"
            src="/logos/logo-wide.png"
            alt="Careva"
            sx={{
              height: 60,
              width: 'auto',
              maxWidth: '100%',
              mb: 2,
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <IconButton onClick={onBack} sx={{ mr: 2 }}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h4" component="h1">
              Create Account
            </Typography>
          </Box>
        </Box>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Complete your registration
        </Typography>

        <form 
          ref={formRef}
          onSubmit={handleFormSubmit} 
          method="post" 
          noValidate 
          autoComplete="on"
        >
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="auth-register-error">
              {apiError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            name="email"
            autoComplete="email"
            sx={{ mb: 3 }}
            helperText="Email address (cannot be changed)"
            InputProps={{
              readOnly: true,
            }}
          />

          <TextField
            fullWidth
            label="First Name"
            value={firstName}
            onChange={handleFirstNameChange}
            error={!!errors.firstName}
            helperText={errors.firstName}
            disabled={isLoading}
            sx={{ mb: 3 }}
            name="firstName"
            autoComplete="given-name"
            autoFocus
          />

          <TextField
            fullWidth
            label="Last Name"
            value={lastName}
            onChange={handleLastNameChange}
            error={!!errors.lastName}
            helperText={errors.lastName}
            disabled={isLoading}
            sx={{ mb: 3 }}
            name="lastName"
            autoComplete="family-name"
          />

          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={handlePasswordChange}
            error={!!errors.password}
            helperText={errors.password || PASSWORD_RULE_HINT}
            disabled={isLoading}
            sx={{ mb: 3 }}
            autoComplete="new-password"
            name="password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            disabled={isLoading}
            sx={{ mb: 3 }}
            autoComplete="new-password"
            name="confirmPassword"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleToggleConfirmPasswordVisibility}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Role Selection */}
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <FormLabel component="legend" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                I am a:
              </FormLabel>
              <Tooltip title="Choose your role to get personalized features and access">
                <IconButton size="small" sx={{ ml: 1 }}>
                  <Help fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            
            <RadioGroup
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value as 'PATIENT' | 'CAREGIVER')}
              sx={{ gap: 2 }}
            >
              <Card 
                variant="outlined" 
                sx={{ 
                  cursor: 'pointer',
                  border: selectedRole === 'PATIENT' ? '2px solid' : '1px solid',
                  borderColor: selectedRole === 'PATIENT' ? 'primary.main' : 'divider',
                  backgroundColor: selectedRole === 'PATIENT' ? 'primary.50' : 'background.paper',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'primary.50',
                  }
                }}
                onClick={() => setSelectedRole('PATIENT')}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <FormControlLabel
                    value="PATIENT"
                    control={<Radio />}
                    label={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Person color="primary" />
                        <Box>
                          <Typography variant="subtitle1" fontWeight="medium" data-testid="register-role-careseeker-label">
                            {CARESEEKER_LABEL}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            I need care and support services
                          </Typography>
                        </Box>
                      </Box>
                    }
                    sx={{ m: 0, width: '100%' }}
                  />
                </CardContent>
              </Card>

              <Card 
                variant="outlined" 
                sx={{ 
                  cursor: 'pointer',
                  border: selectedRole === 'CAREGIVER' ? '2px solid' : '1px solid',
                  borderColor: selectedRole === 'CAREGIVER' ? 'primary.main' : 'divider',
                  backgroundColor: selectedRole === 'CAREGIVER' ? 'primary.50' : 'background.paper',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'primary.50',
                  }
                }}
                onClick={() => setSelectedRole('CAREGIVER')}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <FormControlLabel
                    value="CAREGIVER"
                    control={<Radio />}
                    label={
                      <Box display="flex" alignItems="center" gap={1}>
                        <LocalHospital color="warning" />
                        <Box>
                          <Typography variant="subtitle1" fontWeight="medium">
                            Caregiver
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            I provide care and support services
                          </Typography>
                        </Box>
                      </Box>
                    }
                    sx={{ m: 0, width: '100%' }}
                  />
                </CardContent>
              </Card>
            </RadioGroup>
          </FormControl>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading || hasErrors || !firstName.trim() || !lastName.trim() || !password.trim() || !confirmPassword.trim()}
            sx={{ mb: 2 }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Create Account'
            )}
          </Button>

          <Typography variant="body2" color="text.secondary" align="center">
            Already have an account?{' '}
            <Typography
              component="span"
              variant="body2"
              color="primary"
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={onBack}
            >
              Go back to sign in
            </Typography>
          </Typography>
        </form>
      </Paper>
    </Box>
  );
};
